/**
 * Master-Presence Exception Configs
 *
 * Covers:
 *   - ex-product-ls-missing-helper-products
 *   - ex-product-ls-missing-dosage-name
 *
 * Both masters render through the shared compare-table renderer
 * (``shared/masterCompareTable.ts``). The renderer is field-driven, so
 * the only thing each master supplies is its codes + the compare-field
 * labels. The backend classifier
 * (``backend/shared/masters/presence_classifier.py``) composes the
 * Issue text + Action hint per row, so the frontend just prints what the
 * payload contains.
 */
import {
  buildMasterCompareConfig,
  type CompareField,
} from '../shared/masterCompareTable'
import type { ExceptionColumnDef, ExceptionConfig } from '../types'


// Name first (bold primary line), SKU second (mono secondary line). The
// shared renderer makes idx 0 bold and pulls mono onto idx 1 when the
// field is code-like, so the SKU still reads as a code while the name
// becomes the line the eye lands on.
const LS_PRODUCT_FIELDS: CompareField[] = [
  { key: 'name', label: 'Name' },
  { key: 'handle', label: 'SKU' },
]


// Platform-export-only similarity column. The backend already includes
// ``suggested_match_score`` in the per-code COLUMNS list, so this only
// surfaces the column in the admin preview table (the Inspector context
// hides it - the Issue cell renders "Likely match NN%" inline already).
const platformExtras: ExceptionColumnDef[] = [
  {
    key: 'suggested_match_score',
    label: 'Similarity',
    align: 'right',
    contexts: ['platform'],
  },
]


export const helperProductsPresenceConfig: ExceptionConfig = buildMasterCompareConfig({
  codes: ['ex-product-ls-missing-helper-products'],
  statusSet: 'closed',
  compareFields: LS_PRODUCT_FIELDS,
  systemLabel: 'Lightspeed',
  // SKU (handle) is the master's join key - identical to master_value.
  masterKeyField: 'handle',
  extraPlatformColumns: platformExtras,
})


export const dosageNamePresenceConfig: ExceptionConfig = buildMasterCompareConfig({
  codes: ['ex-product-ls-missing-dosage-name'],
  statusSet: 'closed',
  compareFields: LS_PRODUCT_FIELDS,
  systemLabel: 'Lightspeed',
  // Synthetic SKU is the master's join key after the dosage handle
  // backfill - identical to master_value.
  masterKeyField: 'handle',
  extraPlatformColumns: platformExtras,
})
